import type { Band, ThresholdTable } from '@/lib/thresholds';
import { formatPair } from '@/lib/pairs';

export interface AdviceItem {
  id: string;
  category: 'water' | 'feeding' | 'general';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  action: string;
}

interface ReadingValues {
  ph?: number | null;
  dissolved_oxygen?: number | null;
  temperature_c?: number | null;
  salinity_ppt?: number | null;
  ammonia_ppm?: number | null;
  nitrite_ppm?: number | null;
  alkalinity_ppm?: number | null;
  alkalinity_am?: number | null;
  alkalinity_pm?: number | null;
  alkalinity_2?: number | null;
  transparency_cm?: number | null;
}

const OPEN_BAND: Band = { rec_min: null, rec_max: null, crit_min: null, crit_max: null };
const lo = (v: number, bound: number | null) => bound !== null && v < bound;
const hi = (v: number, bound: number | null) => bound !== null && v > bound;

/**
 * Generate actionable advice from the same recommended bands the entry grid colours by
 * (the farm's threshold table), so a value is never "fine" on one screen and
 * flagged on another. No table yet means no advice. Pure function — no side effects.
 */
export function generateAdvice(
  reading: ReadingValues,
  _doc?: number | null,
  _abw?: number | null,
  table?: ThresholdTable,
): AdviceItem[] {
  if (!table) return [];
  const items: AdviceItem[] = [];
  const band = (column: string) => table[column] ?? OPEN_BAND;
  const phB = band('ph');
  const doB = band('dissolved_oxygen');
  const tempB = band('temperature_c');
  const nh3B = band('ammonia_ppm');
  const no2B = band('nitrite_ppm');
  const alkB = band('alkalinity_ppm');
  const transB = band('transparency_cm');

  const ph = reading.ph;
  const doVal = reading.dissolved_oxygen;
  const temp = reading.temperature_c;
  const ammonia = reading.ammonia_ppm;
  const nitrite = reading.nitrite_ppm;
  const alk = reading.alkalinity_ppm ?? reading.alkalinity_am ?? reading.alkalinity_pm;
  const alk2 = reading.alkalinity_2;
  const trans = reading.transparency_cm;

  // pH checks
  if (ph !== null && ph !== undefined) {
    if (lo(ph, phB.rec_min)) {
      items.push({
        id: 'ph_low',
        category: 'water',
        severity: ph < phB.rec_min! - 0.5 ? 'critical' : 'warning',
        title: `Low pH (${ph.toFixed(1)})`,
        action: 'Apply agricultural lime at 20-30 kg/ha to raise pH gradually',
      });
    } else if (hi(ph, phB.rec_max)) {
      items.push({
        id: 'ph_high',
        category: 'water',
        severity: ph > phB.rec_max! + 0.5 ? 'critical' : 'warning',
        title: `High pH (${ph.toFixed(1)})`,
        action: 'Increase water exchange. Apply molasses (2-5 ppm) or organic matter to lower pH',
      });
    }
  }

  // DO checks
  if (doVal !== null && doVal !== undefined) {
    if (lo(doVal, doB.rec_min)) {
      items.push({
        id: 'do_low',
        category: 'water',
        severity: lo(doVal, doB.crit_min) ? 'critical' : 'warning',
        title: `Low DO (${doVal.toFixed(1)} mg/L)`,
        action: 'Turn on all aerators immediately. Reduce or stop feeding until DO recovers above 5 mg/L',
      });
    }
  }

  // Temperature checks
  if (temp !== null && temp !== undefined) {
    if (hi(temp, tempB.rec_max)) {
      items.push({
        id: 'temp_high',
        category: 'water',
        severity: temp > 34 ? 'critical' : 'warning',
        title: `High temperature (${temp.toFixed(1)}°C)`,
        action: 'Increase water depth if possible. Run aerators during hottest hours. Reduce feeding by 20%',
      });
    } else if (lo(temp, tempB.rec_min)) {
      items.push({
        id: 'temp_low',
        category: 'water',
        severity: temp < 24 ? 'critical' : 'warning',
        title: `Low temperature (${temp.toFixed(1)}°C)`,
        action: 'Reduce feeding by 30-50%. Monitor shrimp activity closely',
      });
    }
  }

  // Ammonia checks
  if (ammonia !== null && ammonia !== undefined) {
    if (hi(ammonia, nh3B.rec_max)) {
      items.push({
        id: 'ammonia_high',
        category: 'water',
        severity: ammonia > nh3B.rec_max! * 2 ? 'critical' : 'warning',
        title: `High ammonia (${ammonia.toFixed(2)} ppm)`,
        action: 'Reduce feeding by 30%. Increase aeration. Apply probiotics. Partial water exchange (10-20%)',
      });
    }
  }

  // Nitrite checks
  if (nitrite !== null && nitrite !== undefined) {
    if (hi(nitrite, no2B.rec_max)) {
      items.push({
        id: 'nitrite_high',
        category: 'water',
        severity: nitrite > no2B.rec_max! * 2 ? 'critical' : 'warning',
        title: `High nitrite (${nitrite.toFixed(2)} ppm)`,
        action: 'Apply salt (NaCl) at 3 ppt to reduce nitrite toxicity. Increase aeration. Reduce feeding',
      });
    }
  }

  // Alkalinity checks
  // Either number of the pair being low is a low alkalinity (each against its own band, which the
  // table gives the second number from the first unless a farm overrides it); the title shows both.
  const alk2B = band('alkalinity_2');
  const alkLow = (alk != null && lo(alk, alkB.rec_min)) || (alk2 != null && lo(alk2, alk2B.rec_min));
  const alkCritical = (alk != null && lo(alk, alkB.crit_min)) || (alk2 != null && lo(alk2, alk2B.crit_min));
  if (alkLow) {
    items.push({
      id: 'alk_low',
      category: 'water',
      severity: alkCritical ? 'warning' : 'info',
      title: `Low alkalinity (${formatPair(alk, alk2)} ppm)`,
      action: 'Apply dolomite or sodium bicarbonate to raise alkalinity above 100 ppm',
    });
  }

  // Transparency checks
  if (trans !== null && trans !== undefined) {
    if (lo(trans, transB.rec_min)) {
      items.push({
        id: 'trans_low',
        category: 'water',
        severity: 'info',
        title: `Low transparency (${trans.toFixed(0)} cm)`,
        action: 'Plankton bloom may be too dense. Partial water exchange recommended',
      });
    } else if (hi(trans, transB.rec_max)) {
      items.push({
        id: 'trans_high',
        category: 'water',
        severity: 'info',
        title: `High transparency (${trans.toFixed(0)} cm)`,
        action: 'Low plankton density. Apply organic fertilizer to promote plankton growth',
      });
    }
  }

  // Feeding advice based on conditions
  if (items.some(i => i.id === 'do_low' || i.id === 'ammonia_high' || i.id === 'temp_high')) {
    items.push({
      id: 'reduce_feed',
      category: 'feeding',
      severity: 'warning',
      title: 'Reduce feeding',
      action: 'Water quality issues detected. Reduce daily feed by 30% until parameters normalize',
    });
  }

  return items;
}
